import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useT } from 'i18n/hooks/useT';
import { CarouselSlideProps } from '../types';
import { SCREEN_BREAKPOINTS } from '../../../constants';

const DESKTOP_SLIDE_OFFSET: number = 100;
const MOBILE_SLIDE_OFFSET: number = 80;

const Slide = styled.div<{
  isActive?: boolean;
  isDisplayed?: boolean;
  leftViewWidth?: any;
}>`
  position: absolute;
  left: ${({ leftViewWidth }) => leftViewWidth?.desktop}vw;
  display: flex;
  justify-content: center;
  min-width: 100%;
  max-width: 100%;
  padding: 45px 6%;
  border-radius: 15px;
  background: #ffffff;
  opacity: ${({ isDisplayed }) => (isDisplayed ? 1 : 0)};
  transition: left 0.75s ease-in-out;
  @media (max-width: ${SCREEN_BREAKPOINTS.TABLET}px) {
    padding: 40px 48px;
  }
  @media (max-width: ${SCREEN_BREAKPOINTS.MOBILE}px) {
    left: ${({ leftViewWidth }) => leftViewWidth?.mobile}vw;
    min-width: 80%;
    max-width: 80%;
    min-height: 300px;
    margin: 0 10%;
    padding: 15px;
  }
`;

const SlideContentContainer = styled.div`
  display: flex;
  justify-content: center;
  svg {
    min-width: 185px;
    max-width: 185px;
    min-height: 185px;
    max-height: 185px;
  }
  a {
    display: inline-block;
    margin-top: 14px;
    color: #292f36;
    font-size: 1rem;
    text-decoration: underline;
  }
  &[data-view='mobile'] {
    display: none;
    svg {
      min-width: 100px;
      max-width: 100px;
      min-height: 100px;
      max-height: 100px;
      margin-right: 15px;
    }
    a {
      font-size: 0.875rem;
    }
  }
  @media (max-width: ${SCREEN_BREAKPOINTS.MOBILE}px) {
    &[data-view='desktop'] {
      display: none;
    }
    &[data-view='mobile'] {
      display: block;
    }
  }
`;

const SlideContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 45px;
  @media (max-width: ${SCREEN_BREAKPOINTS.MOBILE}px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-right: 0;
    margin-bottom: 10px;
  }
`;

const SlideTitle = styled.h3`
  margin: 0 0 25px;
  color: #185941;
  font-weight: 900;
  font-size: 2.25rem;
  line-height: 2.5rem;
  @media (max-width: ${SCREEN_BREAKPOINTS.MOBILE}px) {
    margin: 0;
    font-size: 1.25rem;
    line-height: 1.5rem;
  }
`;

const SlideBody = styled.p`
  margin: 0;
  color: #292f36;
  font-size: 1.25rem;
  line-height: 1.875rem;
  @media (max-width: ${SCREEN_BREAKPOINTS.MOBILE}px) {
    font-size: 0.875rem;
    line-height: 1.375rem;
  }
`;

export const CarouselSlide = ({
  activeAdIndex,
  ad,
  index,
  rotatingAdIndex,
  slideIndexArray,
}: CarouselSlideProps) => {
  const t = useT();
  const slideRef = useRef<HTMLDivElement>(null);
  const isActive = activeAdIndex === index;
  const indexDistance = Math.abs(
    slideIndexArray.indexOf(index) - slideIndexArray.indexOf(activeAdIndex)
  );
  const indexDirection =
    slideIndexArray.indexOf(index) < slideIndexArray.indexOf(activeAdIndex)
      ? -1
      : 1;
  const leftViewWidth = {
    desktop: isActive
      ? 0
      : DESKTOP_SLIDE_OFFSET * indexDistance * indexDirection,
    mobile: isActive ? 0 : MOBILE_SLIDE_OFFSET * indexDistance * indexDirection,
  } as { [key: string]: number };

  // Determines the height dynamically based on what slide is the active slide
  // in the carousel
  useEffect(() => {
    if (slideRef.current) {
      const { ResizeObserver } = window;

      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (!isActive) {
            resizeObserver.unobserve(entry.target);
            return;
          }

          const target = entry.target as HTMLElement;
          if (
            target &&
            target.offsetHeight &&
            target.parentElement &&
            target.offsetHeight !== target.parentElement.offsetHeight
          ) {
            target.parentElement.style.height = `${target.offsetHeight}px`;
          }
        }
      });

      resizeObserver.observe(slideRef.current);
    }
  }, [isActive, slideRef]);
  const {
    getTitle,
    body: { svg, getLanguage },
    learnMoreLink,
  } = ad;
  const title = getTitle(t);
  const language = getLanguage(t);

  return (
    <Slide
      aria-roledescription="slide"
      aria-hidden={!isActive}
      isActive={isActive}
      isDisplayed={rotatingAdIndex !== index}
      leftViewWidth={leftViewWidth}
      ref={slideRef}
      role="group"
    >
      <SlideContentContainer data-view="desktop">
        <SlideContent>
          <SlideTitle>{title}</SlideTitle>
          <SlideBody>{language}</SlideBody>
          {ad.learnMoreLink && (
            <a
              href={learnMoreLink}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={isActive ? 0 : -1}
            >
              {t('Link.LearnMore', 'Learn more', 'Learn more link text')}
            </a>
          )}
        </SlideContent>
        {svg('desktop')}
      </SlideContentContainer>
      <SlideContentContainer data-view="mobile">
        <SlideContent>
          {svg('mobile')}
          <SlideTitle>{title}</SlideTitle>
        </SlideContent>
        <SlideBody>{language}</SlideBody>
        {ad.learnMoreLink && (
          <a
            href={learnMoreLink}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={isActive ? 0 : -1}
          >
            {t('Link.LearnMore', 'Learn more', 'Learn more link text')}
          </a>
        )}
      </SlideContentContainer>
    </Slide>
  );
};
